// API Service for stock
import { notaanota } from './notaanota.js'
import { isAndroid } from '../utils/androidUtils.js'

const API_BASE_URL = import.meta.env.VITE_API_URL 
const API_FALLBACK_URL = import.meta.env.VITE_API_FALLBACK_URL

class ApiService {
  constructor() {
    // Sempre usar a URL do arquivo .env (VITE_API_URL)
    this.baseURL = API_BASE_URL 
    
    // Fallback URLs apenas como backup, sem depender de localStorage
    const envFallback = API_FALLBACK_URL
    const defaultLocal = 'https://localhost:9300'
    
    // Configurar fallback URLs
    const cands = [envFallback, defaultLocal].filter(Boolean)
    this.fallbackURLs = cands.filter((v) => v && v !== this.baseURL)
    this.features = { sisrota: { checked: false, available: false } }
  }

  // Helper method to get auth headers
  // Helper method to check if token is expired (optional)
  isTokenExpired(token) {
    if (!token) return true
    
    try {
      // Decode JWT token to check expiration (basic check)
      const payload = JSON.parse(atob(token.split('.')[1]))
      const currentTime = Date.now() / 1000
      return payload.exp < currentTime
    } catch {
      // If we can't decode the token, consider it invalid
      return true
    }
  }

  // Enhanced getAuthHeaders method
  getAuthHeaders() {
    const token = localStorage.getItem('authToken')
    if (token && !this.isTokenExpired(token)) {
      return {
        Authorization: `Bearer ${token}`
      }
    } else if (token) {
      try {
        localStorage.removeItem('authToken')
        localStorage.removeItem('user')
        window.dispatchEvent(new CustomEvent('auth-token-invalid'))
      } catch (e) { void e }
    }
    return {}
  }

  setBaseURL(newBase) {
    // Apenas atualizar a baseURL, sem armazenar em localStorage
    this.baseURL = newBase
  }

  computeDefault443Fallback(base) {
    try {
      const u = new URL(base)
      if (u.protocol === 'https:' && u.port && u.port !== '443') {
        return `https://${u.hostname}${u.pathname}`
      }
    } catch (e) { void e }
    return null
  }

  // Helper to build query string from params object, cleaning null/undefined/empty values
  buildQueryString(params) {
    if (!params || Object.keys(params).length === 0) return '';
    const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
    const queryString = new URLSearchParams(cleanParams).toString();
    return queryString ? `?${queryString}` : '';
  }

  async ensureSisrotaAvailable() {
    const f = this.features.sisrota
    if (f.checked) return f.available
    try {
      const res = await this.get('/siscep/statistics')
      f.available = !!res
    } catch {
      f.available = false
    } finally {
      f.checked = true
    }
    return f.available
  }

  getCandidateBases() {
    const list = [this.baseURL]
    const default443 = this.computeDefault443Fallback(this.baseURL)
    if (default443 && !list.includes(default443)) list.push(default443)
    for (const fb of this.fallbackURLs) {
      if (fb && !list.includes(fb)) list.push(fb)
    }
    return list
  }

  async fetchWithTimeout(url, options, timeoutMs = 12000) {
    const controller = new AbortController()
    const id = setTimeout(() => controller.abort(), timeoutMs)
    try {
      return await fetch(url, { ...options, signal: controller.signal })
    } finally {
      clearTimeout(id)
    }
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const bases = this.getCandidateBases()
    const config = {
      headers: options.headers || this.getAuthHeaders(),
      ...options
    }

    let lastError = null

    for (let i = 0; i < bases.length; i++) {
      const base = bases[i]
      const url = `${base}${endpoint}`
      try {
        const response = await this.fetchWithTimeout(url, config, isAndroid() ? 8000 : 12000)
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))

          if (response.status === 401) {
            localStorage.removeItem('authToken')
            //localStorage.removeItem('user')
            try {
              window.dispatchEvent(new CustomEvent('auth-token-invalid'))
            } catch (e) { void e }
            const err = new Error(errorData.message || 'Authentication failed')
            err.name = 'UnauthorizedError'
            err.status = 401
            throw err
          }

          throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
        }

        if (base !== this.baseURL) {
          this.setBaseURL(base)
          console.warn(`API base alterada para: ${base}`)
        }

        return await response.json()
      } catch (error) {
        lastError = error
        const isNetwork = error.name === 'AbortError' || 
          error.name === 'TypeError' || 
          error.message?.includes('fetch') || 
          error.message?.includes('Failed to fetch') ||
          error.message?.includes('Network') ||
          error.message?.includes('ERR_NETWORK') ||
          error.message?.includes('ECONNREFUSED') ||
          error.message?.toLowerCase().includes('timed out')

        if (!isNetwork) {
          throw error
        }

        console.error(`Falha de rede ao acessar ${url}:`, error.message)
        if (i < bases.length - 1) {
          console.warn('Tentando fallback de API...')
          continue
        }
      }
    }

    // All attempts failed
    localStorage.removeItem('authToken')
    localStorage.removeItem('user')

    let hint = ''
    try {
      const u = new URL(this.baseURL)
      if (u.port && u.port !== '443') {
        hint = `Possível bloqueio de porta (${u.port}) em redes móveis/WebView. Prefira HTTPS na porta 443.`
      }
    } catch (e) { void e }

    const msg = `Backend não está disponível: ${lastError?.message || 'Erro de conexío'}` + (hint ? ` ${hint}` : '')
    console.error(msg)
    throw new Error(msg)
  }

  // GET request
  async get(endpoint) {
    return this.request(endpoint, {
      method: 'GET',
      headers: this.getAuthHeaders()
    })
  }

  // DELETE request
  async delete(endpoint) {
    const headers = this.getAuthHeaders();
    delete headers['Content-Type']; // Remove Content-Type for DELETE requests
    return this.request(endpoint, { 
      method: 'DELETE',
      headers: headers
    });
  }

  // POST request
  async post(endpoint, data) {
    const options = {
      method: 'POST'
    }
    
    if (data !== undefined && data !== null) {
      options.body = JSON.stringify(data)
      options.headers = {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json'
      }
    } else {
      // For requests without body, don't include Content-Type
      const token = localStorage.getItem('authToken')
      if (token && !this.isTokenExpired(token)) {
        options.headers = {
          Authorization: `Bearer ${token}`
        }
      }
    }
    
    return this.request(endpoint, options)
  }

  // PUT request
  async put(endpoint, data) {
    const options = {
      method: 'PUT'
    }
    
    if (data !== undefined && data !== null) {
      options.body = JSON.stringify(data)
      options.headers = {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json'
      }
    } else {
      // For requests without body, don't include Content-Type
      const token = localStorage.getItem('authToken')
      if (token && !this.isTokenExpired(token)) {
        options.headers = {
          Authorization: `Bearer ${token}`
        }
      }
    }
    
    return this.request(endpoint, options)
  }



  // PATCH request
  async patch(endpoint, data) {
    const options = {
      method: 'PATCH'
    }
    
    if (data !== undefined && data !== null) {
      options.body = JSON.stringify(data)
      options.headers = {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json'
      }
    } else {
      // For requests without body, don't include Content-Type
      const token = localStorage.getItem('authToken')
      options.headers = {
        ...(token && { Authorization: `Bearer ${token}` })
      }
    }
    
    return this.request(endpoint, options)
  }

  // Auth endpoints
  auth = {
    login: (credentials) => this.post('/v1/auth/login', credentials),
    register: (userData) => this.post('/v1/auth/register', userData),
    // forgotPassword: (email) => this.post('/v1/auth/forgot-password', { email }), // Removido - funcionalidade indisponível
    validateResetToken: (token) => this.post('/v1/auth/validate-reset-token', { token }),
    resetPassword: (token, password) => this.post('/v1/auth/reset-password', { token, newPassword: password }),
    refreshToken: () => this.post('/v1/auth/refresh'),
    logout: () => this.post('/v1/auth/logout', {})
  }

  // User endpoints
  users = {
    getProfile: () => this.get('/v1/users/profile'),
    updateProfile: (data) => this.put('/v1/users/profile', data),
    changePassword: (data) => this.patch('/v1/users/change-password', data),
    getUsers: () => this.get('/v1/users'),
    getAllUsersWithInactive: () => this.get('/v1/users/all-with-inactive'),
    getUserById: (id) => this.get(`/v1/users/${id}`),
    createUser: (data) => this.post('/v1/users', data),
    updateUser: (id, data) => this.patch(`/v1/users/${id}`, data),
    deleteUser: (id) => this.delete(`/v1/users/${id}`),
    resetUserPassword: (id, newPassword) => this.patch(`/v1/users/${id}/reset-password`, { newPassword }),
    getRoles: () => this.get('/v1/users/roles/list'),
    getUsersByRole: (role) => this.get(`/v1/users/by-role/${role}`)
  }


  // Notification endpoints
  notifications = {
    getAll: () => this.get('/v1/notifications'),
    markAsRead: (id) => this.patch(`/v1/notifications/${id}/read`),
    markAllAsRead: () => this.patch('/v1/notifications/read-all'),
    delete: (id) => this.delete(`/v1/notifications/${id}`),
    // Push notifications endpoints
    getVapidKey: () => this.get('/v1/notifications/vapid-public-key'),
    subscribe: (data) => this.post('/v1/notifications/subscribe', data),
    unsubscribe: (data) => this.delete('/v1/notifications/unsubscribe', data),
    unsubscribeAll: () => this.delete('/v1/notifications/unsubscribe'),
    hasActive: () => this.get('/v1/notifications/has-active'),
    getSubscriptions: () => this.get('/v1/notifications/subscriptions'),
    getStatus: () => this.get('/v1/notifications/status'),
    sendTest: () => this.post('/v1/notifications/test')
  }

  // Admin endpoints
  admin = {
    // Dashboard statistics
    getDashboardStats: () => this.get('/v1/admin/dashboard/stats'),
    
    // Branch management
    getBranches: () => this.get('/v1/admin/branches'),
    createBranch: (data) => this.post('/v1/admin/branches', data),
    updateBranch: (id, data) => this.patch(`/v1/admin/branches/${id}`, data),
    deleteBranch: (id) => this.delete(`/v1/admin/branches/${id}`),
    
    // User management
    getUsers: () => this.get('/v1/admin/users'),
    createUser: (data) => this.post('/v1/admin/users', data),
    updateUser: (id, data) => this.patch(`/v1/admin/users/${id}`, data),
    deleteUser: (id) => this.delete(`/v1/admin/users/${id}`),
    
    // Login history
    getLoginHistory: (params = {}) => {
      return this.get(`/v1/admin/login-history${this.buildQueryString(params)}`);
    },
    
    // Labels management
    getLabels: (params = {}) => {
      return this.get(`/v1/labels${this.buildQueryString(params)}`);
    },
    generateLabel: (data) => this.post('/v1/labels/generate', data),
    getLabelsStatistics: () => this.get('/v1/labels/statistics'),
    getLabel: (number) => this.get(`/v1/labels/${number}`),

    // Label Templates (Admin uses core endpoints)
    getLabelTemplates: (params = {}) => {
      return this.get(`/v1/label-templates${this.buildQueryString(params)}`);
    },
    createLabelTemplate: (data) => this.post('/v1/label-templates', data),
    updateLabelTemplate: (id, data) => this.patch(`/v1/label-templates/${id}`, data),
    deleteLabelTemplate: (id) => this.delete(`/v1/label-templates/${id}`),
    createLabelTemplateVersion: (id, data) => this.post(`/v1/label-templates/${id}/new-version`, data),
    activateLabelTemplate: (id) => this.post(`/v1/label-templates/${id}/activate`),

    // WebSocket Print - Admin
    getWsStatus: () => this.get('/v1/admin/ws-status'),
    getWsClients: () => this.get('/v1/admin/ws-clients'),
    
    // Rotas management
    getRotas: (params = {}) => {
      return this.get(`/v1/rotas${this.buildQueryString(params)}`);
    },
    createRota: (data) => this.post('/v1/rotas', data),
    updateRota: (id, data) => this.put(`/v1/rotas/${id}`, data),
    deleteRota: (id) => this.delete(`/v1/rotas/${id}`),
    getRota: (id) => this.get(`/v1/rotas/${id}`),
    getRotasByCep: (cep) => this.get(`/v1/rotas/cep/${cep}`),
    getRotasGrouped: (params = {}) => {
      return this.get(`/v1/rotas/grouped${this.buildQueryString(params)}`);
    },
    getRotasStatistics: () => this.get('/v1/rotas/statistics'),
    importRotas: (data, params = {}) => {
      // Adiciona o parâmetro usuario se não estiver presente
      if (!params.usuario) {
        params.usuario = 'sistema';
      }
      return this.post(`/v1/rotas/import${this.buildQueryString(params)}`, data);
    },
    backupRotas: (params = {}) => {
      return this.post(`/v1/rotas/backup${this.buildQueryString(params)}`, {});
    },
    clearBackup: () => this.delete('/v1/rotas/backup/clear'),
    getBackupCount: () => this.get('/v1/rotas/backup/count'),
    clearRotas: (params = {}) => {
      return this.delete(`/v1/rotas/clear${this.buildQueryString(params)}`);
    },

    // Sisrota management
    getSisrotas: async (params = {}) => {
      const ok = await this.ensureSisrotaAvailable()
      if (!ok) return { items: [], total: 0 }
      return this.get(`/v1/siscep${this.buildQueryString(params)}`);
    },
    createSisrota: async (data) => {
      const ok = await this.ensureSisrotaAvailable()
      if (!ok) return { success: false, disabled: true }
      return this.post('/v1/siscep', data)
    },
    updateSisrota: async (id, data) => {
      const ok = await this.ensureSisrotaAvailable()
      if (!ok) return { success: false, disabled: true }
      return this.patch(`/v1/siscep/${id}`, data)
    },
    deleteSisrota: async (id) => {
      const ok = await this.ensureSisrotaAvailable()
      if (!ok) return { success: true }
      return this.delete(`/v1/siscep/${id}`)
    },
    getSisrota: async (id) => {
      const ok = await this.ensureSisrotaAvailable()
      if (!ok) return null
      return this.get(`/v1/siscep/${id}`)
    },
    getSisrotasByCep: async (cep) => {
      const ok = await this.ensureSisrotaAvailable()
      if (!ok) return []
      return this.get(`/v1/siscep/cep/${cep}`)
    },
    getSisrotasStatistics: async () => {
      const ok = await this.ensureSisrotaAvailable()
      if (!ok) return {}
      return this.get('/v1/siscep/statistics')
    },
    validateCepRange: async (data) => {
      const ok = await this.ensureSisrotaAvailable()
      if (!ok) return { valid: false, disabled: true }
      return this.post('/v1/siscep/validate-cep-range', data)
    },
    
    // Palavras management
    getPalavras: () => this.get('/v1/admin/palavras'),
    createPalavra: (data) => this.post('/v1/admin/palavras', data),
    updatePalavra: (palavra, data) => this.patch(`/v1/admin/palavras/${encodeURIComponent(palavra)}`, data),
    deletePalavra: (palavra) => this.delete(`/v1/admin/palavras/${encodeURIComponent(palavra)}`),

    // SSW integration
    getSswConfig: () => this.get('/v1/ssw/config'),
    upsertSswConfig: (data) => this.put('/v1/ssw/config', data),
    deleteSswConfig: () => this.delete('/v1/ssw/config'),
    iniciarSsw: () => this.post('/v1/ssw/iniciar?headless=true'),
    sswStatus: () => this.get('/v1/ssw/status'),
    fecharSsw: () => this.post('/v1/ssw/fechar'),
    sswLogs: () => this.get('/v1/ssw/logs'),
    sswLogsDates: () => this.get('/v1/ssw/logs/dates'),
    sswLogsSearch: (params = {}) => this.get(`/v1/ssw/logs/search${this.buildQueryString(params)}`),
    sswConsultarDanfe: (chave) => this.post('/v1/ssw/danfe', { chave }),

    // Portal Nf-e integration
    getPortalNfeConfig: () => this.get('/v1/portal-nfe-config'),
    createPortalNfeConfig: (data) => this.post('/v1/portal-nfe-config', data),
    updatePortalNfeConfig: (id, data) => this.put(`/v1/portal-nfe-config/${id}`, data),
    deletePortalNfeConfig: (id) => this.delete(`/v1/portal-nfe-config/${id}`),

    // Integration providers
    getIntegrationProviders: () => this.get('/v1/integrations/providers'),
    createIntegrationProvider: (data) => this.post('/v1/integrations/providers', data),
    updateIntegrationProvider: (id, data) => this.put(`/v1/integrations/providers/${id}`, data),
    deleteIntegrationProvider: (id) => this.delete(`/v1/integrations/providers/${id}`),

    // Org integrations
    getOrgIntegrations: () => this.get('/v1/integrations/org'),
    getOrgIntegrationStats: () => this.get('/v1/integrations/org/stats'),
    createOrgIntegration: (data) => this.post('/v1/integrations/org', data),
    updateOrgIntegration: (id, data) => this.put(`/v1/integrations/org/${id}`, data),
    deleteOrgIntegration: (id) => this.delete(`/v1/integrations/org/${id}`),

    // API Key integrations
    getApiKeys: () => this.get('/v1/api-key-integrations'),
    getApiKeyById: (id) => this.get(`/v1/api-key-integrations/${id}`),
    createApiKey: (data) => this.post('/v1/api-key-integrations', data),
    updateApiKey: (id, data) => this.put(`/v1/api-key-integrations/${id}`, data),
    deleteApiKey: (id) => this.delete(`/v1/api-key-integrations/${id}`),
    queryApiKeyNfe: (id, chave) => this.get(`/v1/api-key-integrations/${id}/query/${chave}`),
  }

  // Veiculo endpoints
  veiculo = {
    create: (data) => this.post('/v1/veiculos', data),
    update: (id, data) => this.patch(`/v1/veiculos/${id}`, data),
    delete: (id) => this.delete(`/v1/veiculos/${id}`),
    list: ({ page = 1, limit = 10, placa, branchId } = {}) => {
      const params = new URLSearchParams()
      if (page) params.set('page', page)
      if (limit) params.set('limit', limit)
      if (placa) params.set('placa', placa)
      if (branchId) params.set('branchId', branchId)
      return this.get(`/v1/veiculos?${params.toString()}`)
    },
    getById: (id) => this.get(`/v1/veiculos/${id}`),
    getByPlaca: (placa) => this.get(`/v1/veiculos/placa/${encodeURIComponent(placa)}`),
  }

  // Notas service
  notas = {
    getHistorico: (chave) => this.get(`/v1/notas/historico/${encodeURIComponent(chave)}`),
    buscarDinamica: (params = {}) => {
      return this.get(`/v1/notas/buscar-dinamica${this.buildQueryString(params)}`);
    },
  }

  // Nota Metragem (cubagem)
  notaMetragem = {
    getByChave: (chave) => this.get(`/v1/nota-metragem/${encodeURIComponent(chave)}`),
    upsert: (data) => this.post('/v1/nota-metragem', data),
    list: (params = {}) => {
      const qs = new URLSearchParams(params).toString()
      return this.get(`/v1/nota-metragem${qs ? `?${qs}` : ''}`)
    },
  }

  // VeiculoUso endpoints
  veiculoUso = {
    list: ({ placa, dia, usuarioCodigo } = {}) => {
      const params = new URLSearchParams()
      if (placa) params.set('placa', placa)
      if (dia) params.set('dia', dia)
      if (usuarioCodigo) params.set('usuarioCodigo', usuarioCodigo)
      return this.get(`/v1/veiculo-uso?${params.toString()}`)
    },
    create: (data) => this.post('/v1/veiculo-uso', data),
    get: (id) => this.get(`/v1/veiculo-uso/${id}`),
    update: (id, data) => this.patch(`/v1/veiculo-uso/${id}`, data),
    remove: (id) => this.delete(`/v1/veiculo-uso/${id}`),
  }

  // NotaVeiculoUso endpoints
  notaVeiculoUso = {
    list: ({ placa, dia, veiculoId, usuarioId } = {}) => {
      const params = new URLSearchParams()
      if (placa) params.set('placa', placa)
      if (dia) params.set('dia', dia)
      if (veiculoId) params.set('veiculoId', veiculoId)
      if (usuarioId) params.set('usuarioId', usuarioId)
      const qs = params.toString()
      return this.get(`/v1/nota-veiculo-uso${qs ? `?${qs}` : ''}`)
    },
    search: ({ placa, dia, veiculoId, usuarioId, cnpj } = {}) => {
      const params = new URLSearchParams()
      if (placa) params.set('placa', placa)
      if (dia) params.set('dia', dia)
      if (veiculoId) params.set('veiculoId', veiculoId)
      if (usuarioId) params.set('usuarioId', usuarioId)
      if (cnpj) params.set('cnpj', cnpj)
      const qs = params.toString()
      return this.get(`/v1/nota-veiculo-uso/buscar${qs ? `?${qs}` : ''}`)
    },
    findTotalsPorVeiculo: ({ placa, dia, veiculoId, usuarioId } = {}) => {
      const params = new URLSearchParams()
      if (placa) params.set('placa', placa)
      if (dia) params.set('dia', dia)
      if (veiculoId) params.set('veiculoId', veiculoId)
      if (usuarioId) params.set('usuarioId', usuarioId)
      const qs = params.toString()
      return this.get(`/v1/nota-veiculo-uso/totais${qs ? `?${qs}` : ''}`)
    },
    create: (data) => this.post('/v1/nota-veiculo-uso', data),
    get: (id) => this.get(`/v1/nota-veiculo-uso/${id}`),
    update: (id, data) => this.patch(`/v1/nota-veiculo-uso/${id}`, data),
    remove: (id) => this.delete(`/v1/nota-veiculo-uso/${id}`),
  }

  // Clientes endpoints
  clientes = {
    list: (params = {}) => {
      const qs = new URLSearchParams(params).toString()
      return this.get(`/v1/clientes${qs ? `?${qs}` : ''}`)
    },
    get: (id) => this.get(`/v1/clientes/${id}`),
    create: (data) => this.post('/v1/clientes', data),
    update: (id, data) => this.patch(`/v1/clientes/${id}`, data),
    delete: (id) => this.delete(`/v1/clientes/${id}`),
    search: (query, params = {}) => {
      const qs = new URLSearchParams({ q: query, ...params }).toString()
      return this.get(`/v1/clientes/search${qs ? `?${qs}` : ''}`)
    },
    // Busca cliente por CNPJ (14 dígitos)
    getByCnpj: (cnpj) => this.get(`/v1/clientes/cnpj/${encodeURIComponent(cnpj)}`),
  }

  lotesRelatorio = {
    buscarPorCarro: (params) => {
      const queryString = new URLSearchParams(params).toString();
      return this.get(`/v1/lotes-relatorio/buscar-por-carro${queryString ? `?${queryString}` : ''}`);
    },
    
    criar: (loteData) => {
      return this.post('/v1/lotes-relatorio', loteData);
    },
    
    excluir: (loteId) => {
      return this.delete(`/v1/lotes-relatorio/${loteId}`);
    }
  };

  relatorios = {
    buscarDadosRelatorio1: (params) => {
      const queryString = new URLSearchParams(params).toString();
      return this.get(`/v1/relatorios/operacional/periodo${queryString ? `?${queryString}` : ''}`);
    }
  };

}

// Create and export a singleton instance
const apiService = new ApiService()

export default apiService

// Export individual services for convenience
export const {
  auth,
  users,
  admin,
  lotesRelatorio,
  relatorios,
  notaMetragem,
  notas,
} = apiService

// Re-export notaanota service
export { notaanota }


